<template>
  <div class="xpage-top-details-status-container" v-auto-animate="$animate">
    <div class="xpage-top-details-status">
      <h4 @click="dropdownClicked">
        {{ statusName }}
        <i class="icon-up-open-big" v-if="showStatusDropdown"></i>
        <i class="icon-down-open-big" v-else></i>
      </h4>
      <div class="xheader-search-dropdown" v-if="showStatusDropdown">
        <div class="application-st">
          <form @submit.prevent="submit" autocomplete="off">
            <nav>
              <label
                class="xradio label-text"
                v-for="status in applicationStatuses"
                :key="status.name"
              >
                {{ status.name }}
                <input
                  type="radio"
                  :disabled="status.disabled"
                  name="radio-status"
                  :checked="status.selected"
                  v-on:input="handleInput(status)"
                />
                <span></span>
              </label>
              <div
                v-if="
                  info.newApplicationStatusId !==
                    ApplicationStatusIds.DECLINED &&
                  info.newApplicationStatusId !== ApplicationStatusIds.CANCELLED
                "
              >
                <p>
                  All open stipulations will be cancelled<br />upon completion
                  of this status change.
                </p>
              </div>
              <CancellationReasonForm
                v-show="showCancellationReasonForm"
                ref="cancellationReasonFormRef"
                :statusId="info.newApplicationStatusId"
                :initialStatusId="currentApplicationStatusId"
                @submit="submit"
                @cancel="cancel"
              />

              <div
                v-if="
                  info.newApplicationStatusId !==
                    ApplicationStatusIds.DECLINED &&
                  info.newApplicationStatusId !== ApplicationStatusIds.CANCELLED
                "
              >
                <h3 class="smallcapitals" style="padding-top: 10px">
                  Reason For Change:
                </h3>
                <div>
                  <textarea v-model="info.statusComment" class="reason-input">
                  </textarea>
                </div>
                <div class="xdropdown-buttons">
                  <button @click.prevent="cancel">Cancel</button>
                  <button @click.prevent="submit">Save Changes</button>
                </div>
              </div>
            </nav>
          </form>
        </div>
      </div>
      <!-- Denial Reasons -->
      <div v-if="showDenialReasonForm" class="denial-reason-box">
        <div class="grid-container">
          <!-- Left Column: Denial Reasons -->
          <div class="column denial-reasons-column">
            <span class="label-head"
              >Denial Reason(s)<i style="color: crimson">*</i></span
            >
            <p>Please select a reason for the denial from the list below.</p>
            <!-- New Denial Reason Entry Form -->
            <span class="label-semi-bold">Denial Reason List:</span>
            <div class="new-denial-reason-entry">
              <select
                v-model="selectedReason"
                :disabled="totalDenialReasons >= 4"
              >
                <option :value="null" disabled>Not Selected</option>
                <option v-for="jdt in jdts" :key="jdt.id" :value="jdt.id">
                  {{ jdt.description }}
                </option>
              </select>
            </div>

            <!-- Add Clear Denial Reasons -->
            <div class="add-clear-buttons">
              <button
                @click.prevent="addAnotherReason"
                :disabled="totalDenialReasons >= 4"
                class="add-btn"
              >
                + Add Another Reason
              </button>
            </div>
            <div v-if="totalDenialReasons >= 4" class="max-reasons-message">
              <p>You have reached the maximum of 4 denial reasons.</p>
            </div>
          </div>

          <!-- Right Column: Requested For -->
          <div class="column requested-for-column">
            <span class="label-head"
              >Requested For<i style="color: crimson">*</i></span
            >
            <p>Select at least one applicant to apply the denial reason(s).</p>

            <!-- Applicant Labels -->
            <div class="applicant-labels-row">
              <div
                class="applicant-column"
                v-for="applicant in applicants"
                :key="applicant.id"
              >
                <span class="label-semi-bold">{{
                  applicant.applicantTypeName
                }}</span>
                <div class="checkbox-container">
                  <input
                    type="checkbox"
                    :value="applicant.id"
                    :checked="isApplicantSelectedForReason(applicant.id)"
                    @change="toggleApplicantForReason(applicant.id, $event)"
                    :disabled="!selectedReason"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div class="added-denial-reasons-container">
            <div
              v-if="denialReasonEntries.length > 0"
              class="added-denial-reasons"
            >
              <ul>
                <li
                  v-for="(entry, index) in denialReasonEntries"
                  :key="index"
                  class="added-denial-reason"
                >
                  <div class="reason-content">
                    <span class="reason-number">{{ index + 1 }}.</span>
                    <span class="reason-description">{{
                      entry.reasonId !== null
                        ? getDenialReasonName(entry.reasonId)
                        : "Unknown"
                    }}</span>
                    <span class="reason-applicants">
                      - Applicant:
                      <span>{{
                        getApplicantNames(entry.applicants).join(", ")
                      }}</span></span
                    >
                  </div>
                  <button
                    @click.prevent="removeReason(index)"
                    class="remove-reason"
                  >
                    X
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <h3 class="smallcapitals" style="padding-top: 10px">
            Reason For Change:<i style="color: crimson">*</i>
          </h3>
          <div>
            <base-form-field
              class="reason-input"
              v-model="info.statusComment"
              :schema="schema.statusComment"
            />
          </div>
          <div v-show="otherDenialReasonSelected">
            <h3 class="smallcapitals">
              Other Reason:<i style="color: crimson">*</i>
            </h3>
            <base-form-field
              class="reason-input"
              v-model="info.otherDenialReason"
              :schema="schema.otherDenialReason"
            />
          </div>
          <div class="xdropdown-buttons">
            <button @click.prevent="cancel">Cancel</button>
            <button @click.prevent="submit">Save Changes</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  reactive,
  onBeforeMount,
  watch,
  onMounted,
  nextTick,
  toRefs,
} from "vue";
import { FormSchema, FieldSchema } from "@/models/form";
import { $api } from "@/services/api1";
import { JudgmentalDenial } from "@/models/opal";
import { useApplicationStore } from "@/store/store/applicationVMStore";
import {
  ApplicationStatusIds,
  JudgmentDenialTypeIds,
} from "@/types/application";
import { storeToRefs } from "pinia";
import { Type, LOSType, ProductIds } from "@/types/types";
import { useTypeStore } from "@/store/store/typeStore";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import functions from "@/use/functions";
import useFunctions from "@/use/functions1";
import useValidation from "@/use/validation";
import BaseFormField from "@/components/form-fields/BaseFormField.vue";
import { usePermissionsStore } from "@/store/store/permissionsStore";
import { Role, Roles } from "@/models/permissions";
import CancellationReasonForm from "@/components/v2/application/application-status-form/CancellationReasonForm.vue";
import { useApplicationStatusForm } from "@/composables/application/application-status/useApplicationStatusForm";

interface DenialReasonEntry {
  reasonId: number;
  applicants: number[];
}

const { decodeHTMLEncodedStr } = useFunctions();
const { validate } = useValidation();

const store = useStore();
const route = useRoute();
const applicationStore = useApplicationStore();
const permissionsStore = usePermissionsStore();
const typeStore = useTypeStore();
const { viewModel } = storeToRefs(applicationStore);

const cancellationReasonFormRef = ref<InstanceType<
  typeof CancellationReasonForm
> | null>(null);

// State
let checkboxFieldsSchema = reactive({} as FormSchema);
let denialReasons = reactive({} as any);
let selectedDenialReasons = ref([] as any[]);
let selectedDenialReasonIds = ref([] as number[]);
let reasonIds = ref([] as number[]);

const applicationJudgmentDenials = ref<JudgmentalDenial[]>([]);
const denialReasonEntries = ref<DenialReasonEntry[]>([]);
const selectedReason = ref<number | null>(null);

const previouslySelectedReasonIds = ref<number[]>([]);
const previouslySelectedReasons = ref<string[]>([]);
const showDenialReasonSection = ref<boolean>(false);
const showDenialReasonDropdown = ref<boolean>(false);
const showStatusDropdown = ref<boolean>(false);

const jdts = ref([] as LOSType[]);

const info = reactive({} as any);

const schema = reactive<FormSchema>({
  statusComment: {
    name: "statusComment",
    type: "textarea",
    hideLabel: true,
    required: true,
    errors: [],
    valid: true,
    maxLength: undefined,
    errorMessage: "Reason for change is required.",
  } as FieldSchema,
  otherDenialReason: {
    name: "otherDenialReason",
    type: "textarea",
    hideLabel: true,
    required: false,
    errors: [],
    valid: true,
    maxLength: 128,
    errorMessage: "Other reason is required.",
  } as FieldSchema,
});

const isAdmin = computed<boolean>(
  () =>
    permissionsStore.currentUser.roles?.find(
      (r: Role) => r.id === Roles.Admin,
    ) !== undefined,
);

const applicationId = computed<number>(() => applicationStore.viewModel?.id);

const statusName = computed<string>(
  () => applicationStore.viewModel?.statusName || "",
);

const isLegalPacketSigned = computed<boolean>(
  () => applicationStore.viewModel.isLegalPacketSigned,
);

const statusReasonId = computed(
  () => applicationStore.viewModel.statusReasonId,
);

const isPEL = computed<boolean>(
  () => applicationStore.viewModel.productTypeId === ProductIds.PEL,
);

const isRefiOrPEL = computed(
  () =>
    applicationStore.viewModel.productTypeId === ProductIds.PEL ||
    applicationStore.viewModel.productTypeId === ProductIds.REFI,
);

const applicants = computed(() => applicationStore.Applicants);

const showDenialReasonForm = computed(
  () =>
    showStatusDropdown.value &&
    info.newApplicationStatusId === ApplicationStatusIds.DECLINED,
);

const otherDenialReasonSelected = computed(() =>
  denialReasonEntries.value.some(
    (entry) => entry.reasonId === JudgmentDenialTypeIds.Other,
  ),
);

const totalDenialReasons = computed(() => denialReasonEntries.value.length);

const applicationStatuses = computed<Array<LOSType>>(() =>
  applicationStore.summary.applicationStatuses.filter(
    (s: LOSType) => s.visible === true,
  ),
);

const everHadRightToCancel = computed<boolean>(
  () => applicationStore.summary.everHadRightToCancel,
);

const currentApplicationStatusId = computed<number>(
  () => applicationStore.summary.statusId,
);

const version = ref<number>(2);

const {
  showCancellationReasonForm,
  updateApplicationStatusAsync,
  closeCancellationReasonForm,
} = useApplicationStatusForm({
  info: info,
  version: version,
  applicationId: applicationId,
  cancellationReasonFormRef,
  currentApplicationStatusId,
  onApplicationStatusUpdateSuccess: async () => {
    const referenceId =
      applicationStore.referenceId || route.params.referenceId.toString();

    await Promise.all([
      applicationStore.getApplication(referenceId),
      applicationStore.getApplicationSummary(referenceId),
      applicationStore.getApplicationComments(referenceId),
    ]);
  },
});

const getDenialReasonName = (id: number) => {
  return jdts.value.find((jdt) => jdt.id === id)?.description;
};

async function getDenialReasons() {
  if (currentApplicationStatusId.value === ApplicationStatusIds.DECLINED) {
    let alreadySelectedDenialReasons =
      await $api.applications.getApplicationJudgementDenials(
        applicationStore.applicationId,
      );

    if (
      alreadySelectedDenialReasons &&
      alreadySelectedDenialReasons?.judgmentDenialTypes?.length > 0
    ) {
      showDenialReasonDropdown.value = true;
      applicationJudgmentDenials.value =
        alreadySelectedDenialReasons?.judgmentDenialTypes;

      alreadySelectedDenialReasons?.judgmentDenialTypes?.forEach(
        (jdt: JudgmentalDenial) => {
          previouslySelectedReasons.value.push(jdt.judgmentDenialTypeName);
          previouslySelectedReasonIds.value.push(jdt.judgmentDenialTypeId);

          previouslySelectedReasons.value = [
            ...new Set(previouslySelectedReasons.value),
          ];
          previouslySelectedReasonIds.value = [
            ...new Set(previouslySelectedReasonIds.value),
          ];
        },
      );

      reasonIds.value = previouslySelectedReasonIds.value;
    }
  }
}

async function cancel() {
  showStatusDropdown.value = false;
  showDenialReasonSection.value = false;
  showDenialReasonDropdown.value = false;
  await resetDropdown();
  await nextTick();
}

function resetDropdown() {
  info.newApplicationStatusId = undefined;
  info.newStatusName = undefined;
  info.statusComment = undefined;
  resetDenialReasonsForm();
  resetDenialReasons();
  closeCancellationReasonForm();
  setApplicationStatus();
}

async function setApplicationStatus() {
  info.applicationStatusName = viewModel.value.statusName;

  if (info.applicationStatusName === "Declined") {
    showDenialReasonSection.value = true;
    showDenialReasonDropdown.value = true;
    await getDenialReasons();
  } else {
    resetDenialReasons();
    showDenialReasonSection.value = false;
    showDenialReasonDropdown.value = false;
  }
}

async function updateJudgmentalDenials() {
  const ApplicantJDTypeIds = denialReasonEntries.value
    .flatMap((entry) =>
      entry.applicants.map((applicantId) => ({
        applicantTypeId: applicants.value.find((a) => a.id === applicantId)
          ?.applicantTypeId,
        judgmentDenialTypeId: entry.reasonId,
      })),
    )
    .filter(
      (item) =>
        item.applicantTypeId !== undefined &&
        item.judgmentDenialTypeId !== null,
    );

  const applicationJudgementDenialRequest = {
    applicationId: applicationStore.applicationId,
    ApplicantJDTypeIds,
    otherJudgmentTypeReasonText: info?.otherDenialReason || "",
  };

  const response = await $api.applications.addOrUpdateJudgementDenials(
    applicationStore.applicationId,
    applicationJudgementDenialRequest,
  );

  if (response.errors || !response.applicationId) {
    functions.openModal({
      title: "Error",
      description: "There was an issue updating the Application Status",
      buttons: [
        {
          title: "Close",
          onClick: async () => {
            functions.closeModal();
            await resetDropdown();
          },
        },
      ],
    });
    console.log(`Error: updateJudgmentalDenials()`, response.errors);
    return false;
  } else {
    const referenceId = applicationStore.referenceId;
    await Promise.all([
      applicationStore.getApplication(referenceId),
      applicationStore.getApplicationSummary(referenceId),
      applicationStore.getApplicationComments(referenceId),
    ]);

    return true;
  }
}

async function saveStatusChange() {
  functions.setModalLoading();

  if (info.newApplicationStatusId === ApplicationStatusIds.DECLINED) {
    const applicantJDTypes = denialReasonEntries.value.flatMap((entry) =>
      entry.applicants.map((applicant) => ({
        applicantTypeId: applicants.value.find((a) => a.id === applicant)
          ?.applicantTypeId,
        judgmentDenialTypeId: entry.reasonId,
      })),
    );

    if (applicantJDTypes.length === 0) {
      functions.openModal({
        title: "Alert",
        description: "Please select at least one denial reason.",
      });
      return;
    }
  }

  let response = false;
  if (
    info.newApplicationStatusId &&
    currentApplicationStatusId.value !== info.newApplicationStatusId
  ) {
    response = await updateApplicationStatusAsync();
    if (!response) return;
  }
  if (info.newApplicationStatusId === ApplicationStatusIds.DECLINED) {
    response = await updateJudgmentalDenials();
  }
  if (response) {
    window.scrollTo(0, 0);
    functions.closeModal();
    showStatusDropdown.value = false;
    functions.setModalLoading(false);

    functions.openModal({
      title: "Success!",
      description: "The Application Status has been changed!",
      buttons: [
        {
          title: "Close",
          onClick: async () => {
            functions.closeModal();
            showStatusDropdown.value = false;
            info.statusComment = undefined;
            info.otherDenialReason = undefined;
            denialReasonEntries.value = [];
            showDenialReasonSection.value = false;
          },
        },
      ],
    });
  }
}

function removeReason(index: number) {
  denialReasonEntries.value.splice(index, 1);
}

function addAnotherReason() {
  if (totalDenialReasons.value >= 4) {
    functions.openModal({
      title: "Alert",
      description: "You have reached the maximum of 4 denial reasons.",
    });
    return;
  }
  selectedReason.value = null;
}

function resetDenialReasonsForm() {
  denialReasonEntries.value = [];
  selectedReason.value = null;

  info.otherDenialReason = undefined;
  info.statusComment = undefined;
  schema.statusComment.valid = true;
  schema.statusComment.errors = [];
  schema.otherDenialReason.valid = true;
  schema.otherDenialReason.required = false;
  schema.otherDenialReason.errors = [];
}

function dropdownClicked() {
  if (!applicationStore.summary.disabledStatuses) {
    showStatusDropdown.value = !showStatusDropdown.value;

    resetDenialReasons();
    resetDropdown();

    if (currentApplicationStatusId.value === ApplicationStatusIds.DECLINED) {
      showDenialReasonSection.value = true;
      showDenialReasonDropdown.value = true;
    } else {
      showDenialReasonSection.value = false;
      showDenialReasonDropdown.value = false;
    }
  }
}

function handleInput(event: LOSType) {
  info.newStatusName = event.name;
  info.newApplicationStatusId = event.id;

  const allowedStatuses = [
    ApplicationStatusIds.EVALUATION,
    ApplicationStatusIds.SUBMITTED_AND_PROCESSING,
  ];

  if (info.newApplicationStatusId === ApplicationStatusIds.DECLINED) {
    if (!allowedStatuses.includes(currentApplicationStatusId.value)) {
      functions.openModal({
        title: "Alert",
        description:
          "This status change is not allowed from the current application status",
        buttons: [
          {
            title: "Close",
            onClick: () => {
              functions.closeModal();
              closeDropdown();
              resetDropdown();
            },
          },
        ],
      });
    } else {
      resetDenialReasons();
      showDenialReasonSection.value = true;
      showDenialReasonDropdown.value = true;
    }
  } else if (info.newApplicationStatusId === ApplicationStatusIds.CANCELLED) {
    if (currentApplicationStatusId.value === ApplicationStatusIds.DECLINED) {
      cancellationReasonFormRef.value?.toggleForm(false);
      info.newStatusName = undefined;
      info.newApplicationStatusId = undefined;
      toggleAlertModal("This action is not allowed");
    } else {
      cancellationReasonFormRef.value?.toggleForm(true);
    }
  } else if (
    info.newApplicationStatusId === ApplicationStatusIds.SCHOOL_CERTIFICATION
  ) {
    const allowedStatus = [ApplicationStatusIds.SUBMITTED_AND_PROCESSING];

    if (
      !isAdmin.value ||
      !allowedStatus.includes(currentApplicationStatusId.value) ||
      !isLegalPacketSigned.value
    ) {
      toggleAlertModal("This action is not allowed");
    }
  } else if (
    // for Refi or PEL konly, when attempting to switch to Submit & Processing or Evaluation but was cancelled with a reason (RTC).
    isRefiOrPEL.value &&
    everHadRightToCancel.value &&
    currentApplicationStatusId.value === ApplicationStatusIds.CANCELLED &&
    statusReasonId.value !== null
  ) {
    toggleAlertModal(
      "This action cannot be taken. This application was cancelled by the applicant under the Right to Cancel.",
    );
  } else if (info.newApplicationStatusId === ApplicationStatusIds.COMPLETE) {
    if (isPEL.value && !isAdmin.value) {
      toggleAlertModal("This action is not allowed");
    }
  } else if (
    info.newApplicationStatusId ===
      ApplicationStatusIds.SUBMITTED_AND_PROCESSING ||
    info.newApplicationStatusId === ApplicationStatusIds.EVALUATION ||
    info.newApplicationStatusId === ApplicationStatusIds.NOT_SUBMITTED
  ) {
    if (
      currentApplicationStatusId.value ===
      ApplicationStatusIds.SCHOOL_CERTIFICATION
    ) {
      toggleAlertModal("This action is not allowed");
    }
  } else {
    resetDenialReasons();
    closeCancellationReasonForm();
    showDenialReasonSection.value = false;
    showDenialReasonDropdown.value = false;
    if (info.newApplicationStatusId === currentApplicationStatusId.value) {
      info.newStatusName = undefined;
      info.newApplicationStatusId = undefined;
    } else {
      return;
    }
  }
}

function toggleAlertModal(description: string) {
  functions.openModal({
    title: "Alert",
    description: description,
    buttons: [
      {
        title: "Close",
        onClick: () => {
          functions.closeModal();
          closeDropdown();
          resetDropdown();
        },
      },
    ],
  });
}

function closeDropdown() {
  showStatusDropdown.value = false;
}

function isApplicantSelectedForReason(applicantId: number): boolean {
  if (selectedReason.value == null) {
    return false;
  }
  return denialReasonEntries.value.some(
    (entry) =>
      entry.reasonId === selectedReason.value &&
      entry.applicants.includes(applicantId),
  );
}

function resetDenialReasons() {
  selectedDenialReasons.value = [];
  selectedDenialReasonIds.value = [];

  for (let [key, value] of Object.entries(denialReasons)) {
    denialReasons[key] = false;
  }
}

function getApplicantNames(applicantIds: number[]): string[] {
  return applicants.value
    .filter((applicant) => applicantIds.includes(applicant.id))
    .map((applicant) => applicant.applicantTypeName);
}

function confirmStatusChange() {
  const isDeclinedStatus =
    info.newApplicationStatusId === ApplicationStatusIds.DECLINED ||
    (info.newApplicationStatusId === currentApplicationStatusId.value &&
      currentApplicationStatusId.value === ApplicationStatusIds.DECLINED);

  const description =
    isDeclinedStatus && applicationJudgmentDenials.value.length === 0
      ? "Selected decline reasons will be used on AA Notices and CANNOT be changed once you proceed. Are you sure you want to change this status?"
      : "Are you sure you want to change this status?";

  functions.openModal({
    title: "Confirm",
    description: description,
    buttons: [
      {
        title: "Cancel",
        onClick: () => {
          cancellationReasonFormRef.value?.setSavingChanges(false);
          functions.closeModal();
        },
      },
      { title: "Save Changes", onClick: () => saveStatusChange() },
    ],
  });
}

function submit() {
  const isDeclinedStatus =
    info?.newApplicationStatusId === ApplicationStatusIds.DECLINED ||
    (info?.newStatusName === undefined &&
      currentApplicationStatusId.value === ApplicationStatusIds.DECLINED);

  const cleanedComment = info?.statusComment?.trim() || undefined;
  const cleanedOtherReason = info?.otherDenialReason?.trim() || undefined;
  info.statusComment = cleanedComment;
  info.otherDenialReason = cleanedOtherReason;

  if (isDeclinedStatus) {
    if (denialReasonEntries.value.length === 0) {
      functions.openModal({
        title: "Alert",
        description: "Please select at least one denial reason.",
      });
      return;
    }

    validate(schema.statusComment, info.statusComment);
    validate(schema.otherDenialReason, info.otherDenialReason);
  } else {
    if (
      (!info?.newApplicationStatusId && !info.statusComment) ||
      (info?.newApplicationStatusId === currentApplicationStatusId.value &&
        !info.statusComment)
    ) {
      functions.openModal({
        title: "Alert",
        description: "No changes were made. Do you want to make changes?",
        buttons: [
          {
            title: "Yes",
            onClick: async () => functions.closeModal(),
          },
          {
            title: "No",
            onClick: async () => {
              functions.closeModal();
              showStatusDropdown.value = false;
              await resetDropdown();
            },
          },
        ],
      });
      return;
    }

    if (cleanedComment && cleanedComment.length < 5) {
      functions.openModal({
        title: "Alert",
        description:
          "A comment of at least 5 characters is required to change the status!",
      });
      return;
    }
  }

  if (schema.statusComment.valid && schema.otherDenialReason.valid) {
    confirmStatusChange();
  }
}

function toggleApplicantForReason(applicantId: number, event: Event) {
  if (!selectedReason.value) {
    functions.openModal({
      title: "Alert",
      description: "Please select a denial reason first.",
    });
    (event.target as HTMLInputElement).checked = false;
    return;
  }

  const isChecked = (event.target as HTMLInputElement).checked;

  const existingEntryIndex = denialReasonEntries.value.findIndex(
    (entry) => entry.reasonId === selectedReason.value,
  );

  if (isChecked) {
    const existingEntry = denialReasonEntries.value.some(
      (entry) =>
        entry.reasonId === selectedReason.value &&
        entry.applicants.includes(applicantId),
    );

    if (!existingEntry) {
      denialReasonEntries.value.push({
        reasonId: selectedReason.value as number,
        applicants: [applicantId],
      });
    } else {
      const entryIndex = denialReasonEntries.value.findIndex(
        (entry) =>
          entry.reasonId === selectedReason.value &&
          entry.applicants.includes(applicantId),
      );
      if (entryIndex !== -1) {
        const entry = denialReasonEntries.value[entryIndex];
        entry.applicants = entry.applicants.filter((id) => id !== applicantId);

        if (entry.applicants.length === 0) {
          denialReasonEntries.value.splice(entryIndex, 1);
        }
      }
    }

    if (totalDenialReasons.value > 4) {
      functions.openModal({
        title: "Alert",
        description: "You have reached the maximum of 4 denial reasons.",
      });
    }
  }
}

watch(
  denialReasonEntries,
  (newEntries) => {
    reasonIds.value = newEntries
      .map((entry) => entry.reasonId)
      .filter((id): id is number => id !== null);
  },
  { immediate: true },
);

watch(showDenialReasonForm, (value) => {
  if (value) {
    closeCancellationReasonForm();
    resetDenialReasonsForm();
  }
});

watch(otherDenialReasonSelected, (value) => {
  if (value) {
    schema.otherDenialReason.required = true;
  } else {
    schema.otherDenialReason.required = false;
  }
});

onBeforeMount(() => {
  if (typeStore.getTypes(Type.JudgmentDenials)) {
    typeStore.getTypes(Type.JudgmentDenials).forEach((jdt: any) => {
      checkboxFieldsSchema[jdt.name] = {
        type: "checkbox",
        name: jdt.name,
        label: jdt.description,
        required: false,
        hideLabel: true,
      };
    });
  }
});

onMounted(() => {
  const denialTypes = typeStore.getTypes(Type.JudgmentDenials);
  if (denialTypes) {
    jdts.value = denialTypes.sort((a, b) => {
      const isAOther = a.description?.toLowerCase().includes("other");
      const isBOther = b.description?.toLowerCase().includes("other");
      if (isAOther && !isBOther) return 1;
      if (isBOther && !isAOther) return -1;
      return (a.description ?? "").localeCompare(b.description ?? "");
    });
  }
});
</script>

<style scoped>
.application-st {
  padding: 10px 15px;
}

.applicant-labels-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  text-align: center;
  gap: 10px;
}

.applicant-column {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.add-clear-buttons {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.add-btn {
  background-color: black;
  color: #fff;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  font-weight: 600;
}

.add-btn:disabled {
  background-color: #ddd;
  color: #666;
  cursor: not-allowed;
}

.added-denial-reasons-container {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-top: 10px;
}

.added-denial-reasons ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.added-denial-reasons ul li.added-denial-reason {
  background-color: #fff;
  padding: 10px;
  border: 1px solid #ddd;
  display: flex;
  flex-direction: row;
  gap: 10px;
}

.added-denial-reasons ul li.added-denial-reason button {
  background-color: #ff4d4f;
  color: #fff;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.added-denial-reasons ul li.added-denial-reason button:hover {
  background-color: #ff7875;
}

.added-denial-reasons ul li.added-denial-reason span:first-child {
  font-weight: 600;
}

.checkboxes-row {
  display: flex;
  justify-content: space-between;
}

.checkbox-container {
  padding-top: 10px;
  display: flex;
  align-items: center;
}

.checkbox-container input[type="checkbox"] {
  width: 20px;
  height: 20px;
}

.column {
  width: 48%;
  flex-direction: column;
  display: flex;
}

.denial-reason-box {
  position: absolute;
  top: 0;
  left: calc(100% + 240px);
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid #ddd;
  width: 832px;
  z-index: 1000;
  height: auto;
  overflow: visible;
}

.grid-container {
  display: flex;
  justify-content: space-between;
  gap: 20px;
}

input[type="radio"]:disabled + span {
  opacity: 0.6;
  background-color: #ddd;
}

input[type="checkbox"] {
  width: 20px;
  height: 20px;
  appearance: none;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 3px;
  position: relative;
  cursor: pointer;
}

input[type="checkbox"]:checked {
  background-color: black;
}

input[type="checkbox"]:checked::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 6px;
  width: 6px;
  height: 12px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.label-head {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
}

.label-semi-bold {
  font-size: 14px;
  font-weight: 600;
}

.label-text {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
}

.max-reasons-message {
  color: red;
  font-weight: bold;
  text-align: center;
  width: 100%;
  top: 0;
  left: 0;
}

.new-denial-reason-entry {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
}

.new-denial-reason-entry select {
  flex: 1;
  padding: 5px;
}

p.error {
  text-transform: none !important;
  font-size: 13px !important;
  margin-top: 0 !important;
  margin-bottom: 10px !important;
  font-weight: 700 !important;
  color: red;
}

.remove-reason {
  display: flex;
  justify-content: flex-end;
}

.reason-content {
  flex-grow: 1;
}

.reason-number {
  font-weight: 600;
  margin-left: 5px;
}

.reason-description {
  font-size: 14px;
  font-weight: 600;
  padding-left: 10px;
}

.reason-applicants {
  font-size: 14px;
  padding-left: 5px;
  font-weight: 500;
}

textarea.reason-input {
  width: 100%;
  border: 1px solid #ddd;
  height: 110px;
}

textarea {
  width: 100%;
  height: 100px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: vertical;
}

.xheader-search-dropdown {
  margin-top: 3px;
  border-radius: 10px;
}

.xpage-content {
  font-family: "Montserrat", ariel, sans-serif;
  margin-right: 0%;
}

.xpage-top-details-status-container {
  justify-content: flex-start;
  align-items: flex-start;
  font-family: "Montserrat", ariel, sans-serif;
  font-size: 12px;
  font-weight: 500;
}

.xpage-top-details-status {
  flex: 0 0 0px;
}

.xdropdown-buttons {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.xdropdown-buttons button {
  width: 135px;
  height: 36px;
  margin: 0 5px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
}

.xheader-search-dropdown {
  border-radius: 10px;
}

@media screen and (max-width: 992px) {
  .xpage-top-details-status-container {
    justify-content: center;
  }
}
</style>
